import { AxiosResponse } from 'axios';

import { QueryObserverOptions, useQueryClient } from '@tanstack/react-query';

import { useSkipFeaturesStore } from '../../store';
import { TypeCodeState } from '../../types';
import { API_VERSION_V2 } from '~/constants';
import { axiosInstance } from '~/services/fetchServiceV2';
import { TypeErrorFormat, requestHelper } from '~/utils/fetchHelper';

export type TypeCheckNetworkIPResponse = {
  code: string;
  message: string;
  data: {
    cluster: TypeCodeState;
  };
};

/**
 * @description 네트워크 클러스터 정보 확인 api 요청에 사용되는 커스텀 훅
 * @remarks 요청 헤더에 클러스터 정보가 있으면 해당 클러스터 정보를 반환하고, 없으면 현재 IP 정보에 매핑된 클러스터 정보를 반환합니다.
 */
export const useCheckNetworkIP = () => {
  const queryClient = useQueryClient();

  const requestCheckNetworkIP = async ({
    onSuccess,
    onError,
    queryConfig,
  }: {
    onSuccess?: (res: TypeCheckNetworkIPResponse) => void;
    onError?: (err: TypeErrorFormat) => void;
    queryConfig?: Omit<QueryObserverOptions<AxiosResponse<TypeCheckNetworkIPResponse>>, 'queryKey' | 'queryFn'>;
  }) => {
    const endpoint = '/labor/check-ip';
    const changedNetworkIp = useSkipFeaturesStore.getState().changedNetworkIp;

    const query = queryClient.ensureQueryData<AxiosResponse<TypeCheckNetworkIPResponse>>({
      queryKey: ['labor', 'check-ip', changedNetworkIp?.code],
      queryFn: () =>
        axiosInstance.get(endpoint, {
          headers: {
            version: API_VERSION_V2,
            ...(changedNetworkIp !== null && { 'Want-Cluster-Response': changedNetworkIp.code }),
          },
        }),
      ...queryConfig,
      gcTime: 0,
    });

    return requestHelper(() => query, onSuccess, onError);
  };

  return requestCheckNetworkIP;
};
